import { useState, useEffect, useCallback } from 'react';
import { api } from '../api/client';

function emptyForm(fields) {
  const form = {};
  fields.forEach((f) => { form[f.key] = ''; });
  return form;
}

function isVideoUrl(url) {
  return /\.(mp4|webm|ogg|mov)$/i.test(url || '');
}

function ResourceManager({ resource, title, fields, uploadSection }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyForm(fields));
  const [uploading, setUploading] = useState(false);

  const mediaField = fields.find((f) => f.type === 'image' || f.type === 'media');

  const load = useCallback(() => {
    setLoading(true);
    api.list(resource)
      .then((data) => { setItems(data); setError(''); })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [resource]);

  useEffect(() => { load(); }, [load]);

  const startNew = () => {
    setEditingId('new');
    setFormData(emptyForm(fields));
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    const form = {};
    fields.forEach((f) => { form[f.key] = item[f.key] ?? ''; });
    setFormData(form);
  };

  const cancel = () => {
    setEditingId(null);
    setFormData(emptyForm(fields));
  };

  const handleFileChange = async (field, file) => {
    if (!file) return;
    setUploading(true);
    try {
      const { url, mediaType } = await api.upload(uploadSection, file);
      setFormData((prev) => ({
        ...prev,
        [field.key]: url,
        ...(field.typeKey ? { [field.typeKey]: mediaType } : {})
      }));
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    try {
      if (editingId === 'new') {
        await api.create(resource, formData);
      } else {
        await api.update(resource, editingId, formData);
      }
      cancel();
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Supprimer cet élément ?')) return;
    try {
      await api.remove(resource, id);
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const move = async (id, direction) => {
    try {
      await api.move(resource, id, direction);
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const renderForm = () => (
    <div className="admin-form">
      {fields.map((f) => {
        if (f.type === 'hidden') return null;
        const isVideo = f.type === 'media' && (
          (f.typeKey && formData[f.typeKey] === 'video') || isVideoUrl(formData[f.key])
        );
        return (
          <div className="admin-form-row" key={f.key}>
            <label>{f.label}</label>
            {f.type === 'textarea' && (
              <textarea
                value={formData[f.key]}
                onChange={(e) => setFormData((prev) => ({ ...prev, [f.key]: e.target.value }))}
                rows={4}
              />
            )}
            {f.type === 'text' && (
              <input
                type="text"
                value={formData[f.key]}
                onChange={(e) => setFormData((prev) => ({ ...prev, [f.key]: e.target.value }))}
              />
            )}
            {f.type === 'image' && (
              <div>
                {formData[f.key] && (
                  <img src={formData[f.key]} alt="" className="admin-image-preview" />
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileChange(f, e.target.files[0])}
                />
                {uploading && <span> Envoi en cours…</span>}
              </div>
            )}
            {f.type === 'media' && (
              <div>
                {formData[f.key] && (
                  isVideo ? (
                    <video src={formData[f.key]} className="admin-image-preview" muted controls preload="metadata" />
                  ) : (
                    <img src={formData[f.key]} alt="" className="admin-image-preview" />
                  )
                )}
                <input
                  type="file"
                  accept="image/*,video/*"
                  onChange={(e) => handleFileChange(f, e.target.files[0])}
                />
                {uploading && <span> Envoi en cours…</span>}
                <p className="admin-hint">Vidéo : sera lue automatiquement sur le site, sans son.</p>
              </div>
            )}
          </div>
        );
      })}
      <div className="admin-form-actions">
        <button className="admin-btn admin-btn-primary" onClick={save} disabled={uploading}>Enregistrer</button>
        <button className="admin-btn" onClick={cancel}>Annuler</button>
      </div>
    </div>
  );

  return (
    <div className="admin-panel">
      <div className="admin-panel-header">
        <h2>{title}</h2>
        {editingId === null && (
          <button className="admin-btn admin-btn-primary" onClick={startNew}>+ Ajouter</button>
        )}
      </div>
      {error && <div className="admin-error">{error}</div>}
      {editingId === 'new' && renderForm()}
      {loading ? (
        <p>Chargement…</p>
      ) : (
        <ul className="admin-list">
          {items.map((item, index) => {
            const mediaUrl = mediaField && item[mediaField.key];
            const isVideo = mediaField && mediaUrl && (
              (mediaField.typeKey && item[mediaField.typeKey] === 'video') || isVideoUrl(mediaUrl)
            );
            return (
              <li key={item.id} className="admin-list-item">
                {editingId === item.id ? (
                  renderForm()
                ) : (
                  <>
                    <div className="admin-list-item-content">
                      {mediaUrl && (
                        isVideo ? (
                          <video src={mediaUrl} className="admin-image-thumb" muted preload="metadata" />
                        ) : (
                          <img src={mediaUrl} alt="" className="admin-image-thumb" />
                        )
                      )}
                      <div>
                        {fields.filter((f) => f.type !== 'image' && f.type !== 'media' && f.type !== 'hidden').map((f) => (
                          <div key={f.key} className="admin-list-item-field">
                            <strong>{f.label} :</strong> {String(item[f.key] ?? '').slice(0, 140)}
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="admin-list-item-actions">
                      <button className="admin-btn" onClick={() => move(item.id, 'up')} disabled={index === 0}>↑</button>
                      <button className="admin-btn" onClick={() => move(item.id, 'down')} disabled={index === items.length - 1}>↓</button>
                      <button className="admin-btn" onClick={() => startEdit(item)}>Modifier</button>
                      <button className="admin-btn admin-btn-danger" onClick={() => remove(item.id)}>Supprimer</button>
                    </div>
                  </>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default ResourceManager;
